import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header'
import TopicAudioManager from './components/topic/TopicAudioManager'
import CourseReviewModal from './components/review/CourseReviewModal'
import { reviewColumns } from './components/review/reviewSummary'
import { getCourseReviewSummary } from './api/reviews'
import CourseDurationModal from './components/duration/CourseDurationModal'
import CourseKeywordsModal, { parseKeywords } from './components/keywords/CourseKeywordsModal'
import Loading from './components/Loading'
import AnimatedDots from './components/AnimatedDots'
import formatRelativeDate from './services/formatRelativeDate'
import fetchAllCourses from './api/courses'

// --- Column toggle config ---

const ALL_COLUMNS = [
    { key: 'course', label: 'Course', alwaysVisible: true },
    { key: 'audio', label: 'Audio' },
    { key: 'review', label: 'Review' },
    { key: 'duration', label: 'Duration' },
    { key: 'keywords', label: 'Keywords' },
    { key: 'hooks', label: 'Hooks' },
    { key: 'lastReview', label: 'Last Review' },
    { key: 'categories', label: 'Categories' },
    { key: 'status', label: 'Status' },
    { key: 'published', label: 'Published' },
    { key: 'lastEdited', label: 'Last Edited' },
];

const DEFAULT_VISIBLE = ['course', 'audio', 'review', 'duration', 'keywords', 'hooks', 'lastReview', 'categories', 'status'];

// v4: bump when adding new default-visible columns, so saved
// preferences from before the column existed don't hide it
const STORAGE_KEY = 'manageCourses_visibleColumns_v4';

function loadVisibleColumns() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE;
}

// --- Styles ---

const styles = {
    dot: (color) => ({
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: 6,
        verticalAlign: 'middle',
    }),
    newPill: {
        display: 'inline-block',
        background: '#dcfce7',
        color: '#16a34a',
        borderRadius: 4,
        padding: '1px 6px',
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        marginLeft: 6,
        verticalAlign: 'middle',
    },
    starIcon: {
        color: '#d97706',
        fontSize: 12,
        marginRight: 4,
        verticalAlign: 'middle',
    },
    courseSub: {
        fontSize: 11,
        color: '#a1a1aa',
        marginTop: 2,
    },
    courseIdSpan: {
        opacity: 0,
        transition: 'opacity 0.15s',
    },
    categoryPill: {
        display: 'inline-block',
        background: '#f4f4f5',
        color: '#71717a',
        borderRadius: 4,
        padding: '1px 6px',
        fontSize: 11,
    },
    categoryMore: {
        display: 'inline-block',
        marginLeft: 4,
        fontSize: 11,
        color: '#a1a1aa',
        cursor: 'default',
    },
    toggleWrap: { position: 'relative', display: 'inline-block' },
    toggleBtn: {
        background: 'none',
        border: '1px solid #e4e4e7',
        borderRadius: 6,
        padding: '5px 8px',
        cursor: 'pointer',
        fontSize: 14,
        color: '#71717a',
        display: 'flex',
        alignItems: 'center',
    },
    toggleDropdown: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        background: '#fff',
        border: '1px solid #e4e4e7',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        padding: '6px 0',
        zIndex: 100,
        minWidth: 170,
    },
    toggleItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 12px',
        fontSize: 12,
        cursor: 'pointer',
        userSelect: 'none',
        color: '#18181b',
    },
    audioClickable: {
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'left',
    },
    reviewCell: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
    },
    // Review status over the proofread count, so the column stays narrow.
    reviewStack: {
        display: 'inline-flex',
        flexDirection: 'column',
    },
    typos: {
        color: '#0f766e',
        fontSize: '0.85em',
    },
};

export const ManageCourses = () => {
    const [courses, setCourses] = useState([])
    const [first, setFirst] = useState(0);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [isAudioManagerVisible, setAudioManagerVisible] = useState(false);
    const [audioCourse, setAudioCourse] = useState(null);
    const [reviewCourse, setReviewCourse] = useState(null);
    const [durationCourse, setDurationCourse] = useState(null);
    const [keywordsCourse, setKeywordsCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
    const [showColumnToggle, setShowColumnToggle] = useState(false);
    const toggleRef = useRef(null);
    const { user } = useContext(UserContext);
    const toastRef = useRef(null);

    // Close column toggle on outside click
    useEffect(() => {
        if (!showColumnToggle) return;
        const handler = (e) => {
            if (toggleRef.current && !toggleRef.current.contains(e.target)) {
                setShowColumnToggle(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showColumnToggle]);

    const toggleColumn = (key) => {
        setVisibleColumns(prev => {
            const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    };

    const isVisible = (key) => visibleColumns.includes(key);

    const getCourses = useCallback(() => fetchAllCourses(user.token), [user.token])

    const getCategories = useCallback(async () => {
        const res = await axios.get(`/api/courses/categories`, {
            headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' }
        });
        return res.data.items;
    }, [user.token])

    const getCourseTopics = useCallback(async (courseId) => {
        let page = 1;
        const perPage = 100;
        const aggregated = [];

        while (true) {
            const { data } = await axios.get(`/api/courses/${courseId}/topics`, {
                headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' },
                params: { page, per_page: perPage }
            });

            aggregated.push(...(data.items || []));
            const totalPages = data?._meta?.total_pages || 1;
            if (page >= totalPages) {
                break;
            }
            page += 1;
        }

        return aggregated;
    }, [user.token])

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            const categories_data = await getCategories();
            const course_data = await getCourses();

            // Fetch topics and review summaries for all courses in parallel
            const [topicsByCourse, reviewsByCourse] = await Promise.all([
                Promise.all(
                    course_data.map(async (course) => {
                        const topics = await getCourseTopics(course.id);
                        return { courseId: course.id, topics };
                    })
                ),
                Promise.all(
                    course_data.map(async (course) => {
                        try {
                            return { courseId: course.id, summary: await getCourseReviewSummary(user.token, course.id) };
                        } catch {
                            return { courseId: course.id, summary: null };
                        }
                    })
                ),
            ]);

            const data_modified = course_data.map(course => {
                const topicsEntry = topicsByCourse.find(({ courseId }) => courseId === course.id);
                const topics = topicsEntry ? topicsEntry.topics : [];
                const reviewEntry = reviewsByCourse.find(({ courseId }) => courseId === course.id);
                const review = reviewEntry?.summary;
                const totalTopics = topics.length;
                const audioTopics = topics.filter((topic) => topic.has_audio);
                const audioCount = audioTopics.length;
                // Audio is stale only when spoken content changed after
                // generation — metadata edits (keywords, categories, cover)
                // bump last_edited but not content_updated_at.
                const contentUpdatedAt = course['content_updated_at'] || course['last_edited'] || 0;
                const hasAllAudio = totalTopics > 0 && audioCount === totalTopics;
                const allSynced = hasAllAudio && audioTopics.every((topic) => {
                    const generatedAt = topic.audio_generated_at || 0;
                    return generatedAt >= contentUpdatedAt;
                });
                const audioStatus = audioCount === 0
                    ? 'no-audio'
                    : allSynced
                        ? 'synced'
                        : 'out-of-sync';
                const audioStatusSort = audioStatus === 'synced'
                    ? 2
                    : audioStatus === 'out-of-sync'
                        ? 1
                        : 0;
                const doNotMiss = !!course['do_not_miss'];
                const categoriesNames = getCategoriesNames(course.categories, categories_data);
                const keywordsList = parseKeywords(course['search_keywords']);
                const hooks = course.hooks || [];
                const hooksApproved = hooks.filter(h => h.status === 'approved').length;

                return {
                    ...course,
                    topic_count: totalTopics,
                    do_not_miss_flag: doNotMiss,
                    is_new_flag: !!course['is_new'],
                    audio_count: audioCount,
                    audio_total: totalTopics,
                    audio_status: audioStatus,
                    audio_status_sort: audioStatusSort,
                    ...reviewColumns(review),
                    last_edited_ts: course['last_edited'] || null,
                    published_ts: course['published_date'] || null,
                    categories_names: categoriesNames,
                    keywords_list: keywordsList,
                    keywords_count: keywordsList.length,
                    hooks_total: hooks.length,
                    hooks_approved: hooksApproved,
                    hooks_draft: hooks.length - hooksApproved,
                    // approved first, then draft-only, then none
                    hooks_sort: hooksApproved > 0 ? 2 : hooks.length > 0 ? 1 : 0,
                }
            });
            setCourses(data_modified);
            setLoading(false);
        }
        fetchData();
    }, [getCategories, getCourses, getCourseTopics])

    const getCategoriesNames = (categoriesIDs, categories_data) => {
        return categoriesIDs
            .map(categoryID => {
                const category = categories_data.find(({ id }) => id === categoryID)
                return category ? category['name'] : null;
            })
            .filter(Boolean);
    }

    const openAudioManager = (course) => {
        setAudioCourse({ id: course.id, name: course.name });
        setAudioManagerVisible(true);
    }

    const openReviewModal = (course) => {
        setReviewCourse({ id: course.id, name: course.name });
    }

    const openDurationModal = (course) => {
        setDurationCourse({ id: course.id, name: course.name });
    }

    const openKeywordsModal = (course) => {
        setKeywordsCourse({
            id: course.id,
            name: course.name,
            searchKeywords: course.search_keywords || '',
        });
    }

    const handleKeywordsModalClose = useCallback(({ courseId, updated, searchKeywords }) => {
        setKeywordsCourse(null);
        if (updated && courseId) {
            const keywordsList = parseKeywords(searchKeywords);
            setCourses(prev => prev.map(course => (
                course.id === courseId
                    ? {
                        ...course,
                        search_keywords: searchKeywords,
                        keywords_list: keywordsList,
                        keywords_count: keywordsList.length,
                    }
                    : course
            )));
        }
    }, []);

    const handleDurationModalClose = useCallback(({ courseId, updated, courseDuration }) => {
        setDurationCourse(null);
        if (updated && courseId && courseDuration != null) {
            setCourses(prev => prev.map(course => (
                course.id === courseId ? { ...course, duration: courseDuration } : course
            )));
        }
    }, []);

    const refreshCourseReviewSummary = useCallback(async (courseId) => {
        if (!courseId) return;
        try {
            const review = await getCourseReviewSummary(user.token, courseId);
            setCourses(prev => prev.map(course => (
                course.id === courseId ? { ...course, ...reviewColumns(review) } : course
            )));
        } catch { /* ignore — modal already shown its result */ }
    }, [user.token]);

    const handleReviewModalClose = useCallback(({ courseId, ranBatch }) => {
        setReviewCourse(null);
        if (ranBatch) {
            refreshCourseReviewSummary(courseId);
        }
    }, [refreshCourseReviewSummary]);

    // --- Column body templates ---

    const courseBodyTemplate = (rowData) => {
        return (
            <div>
                <div>
                    {rowData.do_not_miss_flag && (
                        <i className="pi pi-star-fill" style={styles.starIcon} />
                    )}
                    <a href={`/editor?course=${rowData.id}`}>{rowData.name}</a>
                    {rowData.is_new_flag && (
                        <span style={styles.newPill}>NEW</span>
                    )}
                </div>
                <div style={styles.courseSub}>
                    <span className="mc-course-id" style={styles.courseIdSpan}>ID: {rowData.id} · </span>
                    {rowData.topic_count} topics
                </div>
            </div>
        );
    };

    const audioBodyTemplate = (rowData) => {
        const dotColor = rowData.audio_status === 'synced' ? '#16a34a'
            : rowData.audio_status === 'out-of-sync' ? '#d97706'
            : '#a1a1aa';
        const label = rowData.audio_status === 'synced' ? 'synced'
            : rowData.audio_status === 'out-of-sync' ? 'out of sync'
            : 'no audio';
        const text = `${rowData.audio_count}/${rowData.audio_total} ${label}`;
        const clickable = rowData.audio_status !== 'synced';

        return (
            <span style={{ whiteSpace: 'nowrap' }}>
                <span style={styles.dot(dotColor)} />
                {clickable ? (
                    <button
                        type="button"
                        onClick={() => openAudioManager(rowData)}
                        style={styles.audioClickable}
                    >
                        {text}
                    </button>
                ) : text}
            </span>
        );
    };

    const reviewBodyTemplate = (rowData) => {
        let statusEl;
        if (rowData.review_status === 'no-cards') {
            statusEl = <span style={{ color: '#a1a1aa' }}>-</span>;
        } else if (rowData.review_status === 'not-reviewed') {
            statusEl = <span style={{ color: '#a1a1aa' }}>Not reviewed</span>;
        } else if (rowData.review_status === 'has-issues') {
            statusEl = (
                <span style={{ color: '#dc3545' }}>
                    {rowData.review_reviewed}/{rowData.review_total} · {rowData.review_unresolved} issues
                </span>
            );
        } else if (rowData.review_status === 'partial') {
            statusEl = <span style={{ color: '#d97706' }}>{rowData.review_reviewed}/{rowData.review_total} partial</span>;
        } else {
            statusEl = <span style={{ color: '#16a34a' }}>{rowData.review_reviewed}/{rowData.review_total} reviewed</span>;
        }

        return (
            <span style={styles.reviewCell}>
                <span style={styles.reviewStack}>
                    {statusEl}
                    {rowData.review_typos > 0 && (
                        <span style={styles.typos} title="Unresolved proofread findings">
                            {rowData.review_typos} typos
                        </span>
                    )}
                </span>
                {rowData.review_status !== 'no-cards' && (
                    <button
                        type="button"
                        onClick={() => openReviewModal(rowData)}
                        className="mc-cell-action"
                        title="Run review for all cards in this course"
                    >
                        <i className="pi pi-play" />
                    </button>
                )}
            </span>
        );
    };

    const durationBodyTemplate = (rowData) => {
        return (
            <span style={styles.reviewCell}>
                <span>{rowData.duration != null ? `${rowData.duration} min` : '-'}</span>
                <button
                    type="button"
                    onClick={() => openDurationModal(rowData)}
                    className="mc-cell-action"
                    title="Review estimated reading time for this course's topics"
                >
                    <i className="pi pi-refresh" />
                </button>
            </span>
        );
    };

    const keywordsBodyTemplate = (rowData) => {
        const count = rowData.keywords_count;
        if (!count) {
            return (
                <span style={styles.reviewCell}>
                    <span style={styles.dot('#a1a1aa')} />
                    <span style={{ color: '#a1a1aa' }}>Not set</span>
                    <button
                        type="button"
                        onClick={() => openKeywordsModal(rowData)}
                        className="mc-cell-action"
                        title="Add search keywords for this course"
                    >
                        <i className="pi pi-plus" />
                    </button>
                </span>
            );
        }
        const cls = `mc-kw-${rowData.id}`;
        return (
            <span style={styles.reviewCell}>
                <span style={styles.dot('#16a34a')} />
                <span className={cls} data-pr-tooltip={rowData.keywords_list.join(', ')} data-pr-position="top">
                    {count} {count === 1 ? 'keyword' : 'keywords'}
                </span>
                <Tooltip target={`.${cls}`} />
                <button
                    type="button"
                    onClick={() => openKeywordsModal(rowData)}
                    className="mc-cell-action"
                    title="Edit search keywords for this course"
                >
                    <i className="pi pi-pencil" />
                </button>
            </span>
        );
    };

    // Hooks (CONTEXT.md): only approved ones reach the app, so the
    // colour follows the approved count; the link opens the Hooks page
    // filtered to this course.
    const hooksBodyTemplate = (rowData) => {
        const href = `/manage-hooks?course=${rowData.id}`;
        if (!rowData.hooks_total) {
            return (
                <span style={styles.reviewCell}>
                    <span style={styles.dot('#a1a1aa')} />
                    <a href={href} style={{ color: '#a1a1aa' }}>None</a>
                </span>
            );
        }
        const color = rowData.hooks_approved > 0 ? '#16a34a' : '#d97706';
        const parts = [];
        if (rowData.hooks_approved) parts.push(`${rowData.hooks_approved} approved`);
        if (rowData.hooks_draft) parts.push(`${rowData.hooks_draft} draft`);
        return (
            <span style={styles.reviewCell}>
                <span style={styles.dot(color)} />
                <a href={href} style={{ color: 'inherit' }}>{parts.join(' · ')}</a>
            </span>
        );
    };

    const dateBodyTemplate = (field) => (rowData) => {
        const ts = rowData[field];
        if (!ts) return <span style={{ color: '#a1a1aa' }}>-</span>;
        const { short, full } = formatRelativeDate(ts);
        const cls = `mc-date-${field}-${rowData.id}`;
        return (
            <>
                <span className={cls} data-pr-tooltip={full} data-pr-position="top">
                    {short}
                </span>
                <Tooltip target={`.${cls}`} />
            </>
        );
    };

    const lastReviewBody = dateBodyTemplate('review_last_ts');
    const publishedBody = dateBodyTemplate('published_ts');
    const lastEditedBody = dateBodyTemplate('last_edited_ts');

    const categoriesTemplate = (rowData) => {
        const cats = rowData.categories_names || [];
        if (cats.length === 0) return <span style={{ color: '#a1a1aa' }}>-</span>;
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {cats.map((cat, i) => (
                    <span key={i} style={styles.categoryPill}>{cat}</span>
                ))}
            </div>
        );
    };

    // --- Header with search + column toggle ---

    const renderHeader = () => {
        return (
            <div className="table-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>List of Topics</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Global Search" />
                    </span>
                    <div style={styles.toggleWrap} ref={toggleRef}>
                        <button
                            type="button"
                            style={styles.toggleBtn}
                            onClick={() => setShowColumnToggle(prev => !prev)}
                            title="Toggle columns"
                        >
                            <i className="pi pi-cog" />
                        </button>
                        {showColumnToggle && (
                            <div style={styles.toggleDropdown}>
                                {ALL_COLUMNS.map(col => (
                                    <label key={col.key} style={styles.toggleItem}>
                                        <input
                                            type="checkbox"
                                            checked={isVisible(col.key)}
                                            disabled={col.alwaysVisible}
                                            onChange={() => toggleColumn(col.key)}
                                        />
                                        {col.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const header = renderHeader();

    return (
        <div className="App">
            <Header />
            <style>{`
                .p-datatable-row-class .mc-course-id,
                .p-datatable tbody tr .mc-course-id {
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .p-datatable tbody tr:hover .mc-course-id {
                    opacity: 1;
                }
                .mc-cell-action {
                    background: none;
                    border: none;
                    padding: 3px 5px;
                    border-radius: 4px;
                    cursor: pointer;
                    color: #a1a1aa;
                    line-height: 1;
                    display: inline-flex;
                    align-items: center;
                    opacity: 0;
                    transition: opacity 0.15s, background 0.15s, color 0.15s;
                }
                .mc-cell-action .pi {
                    font-size: 11px;
                }
                .p-datatable tbody tr:hover .mc-cell-action {
                    opacity: 1;
                }
                .mc-cell-action:hover {
                    background: #f4f4f5;
                    color: #18181b;
                }
            `}</style>
            <div className="container-fluid">
                <div className="row mt-4">
                    <div className="col-2">
                    </div>
                    <div className="col-6 text-center">
                        <h2>
                            <div className="text-align-center">
                                Nerdish Topics
                            </div>
                        </h2>
                    </div>
                </div>
                <div className="row mt-4">
                    <div className="col-12">
                        <DataTable
                            style={{ width: '90%', margin: '0 auto' }}
                            responsiveLayout="scroll"
                            header={header}
                            value={courses}
                            paginator={!loading}
                            rows={20}
                            first={first}
                            onPage={(e) => setFirst(e.first)}
                            globalFilter={globalFilter}
                            globalFilterFields={['name', 'id', 'status', 'search_keywords']}
                            emptyMessage={
                                loading ? (
                                    <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                                        <Loading />
                                        <p style={{ marginTop: '1.5rem', color: '#6c757d', fontSize: '1.1rem', fontWeight: '500' }}>
                                            Loading topics<AnimatedDots />
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                                        No topics found
                                    </div>
                                )
                            }
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown">
                            <Column field="name" header="Course" body={courseBodyTemplate} sortable style={{ minWidth: '300px', width: '30%' }} />
                            {isVisible('audio') && <Column field="audio_status" sortField="audio_status_sort" header="Audio" body={audioBodyTemplate} sortable />}
                            {isVisible('review') && <Column field="review_status" sortField="review_status_sort" header="Review" body={reviewBodyTemplate} sortable />}
                            {isVisible('duration') && <Column field="duration" header="Duration" body={durationBodyTemplate} sortable />}
                            {isVisible('keywords') && <Column field="keywords_count" header="Keywords" body={keywordsBodyTemplate} sortable />}
                            {isVisible('hooks') && <Column field="hooks_total" sortField="hooks_sort" header="Hooks" body={hooksBodyTemplate} sortable />}
                            {isVisible('lastReview') && <Column field="review_last_ts" header="Last Review" body={lastReviewBody} sortable />}
                            {isVisible('categories') && <Column field="categories_names" header="Categories" body={categoriesTemplate} />}
                            {isVisible('status') && <Column field="status" header="Status" sortable />}
                            {isVisible('published') && <Column field="published_ts" header="Published" body={publishedBody} sortable />}
                            {isVisible('lastEdited') && <Column field="last_edited_ts" header="Last Edited" body={lastEditedBody} sortable />}
                        </DataTable>
                    </div>
                </div>
            </div>
            <TopicAudioManager
                visible={isAudioManagerVisible}
                onHide={() => setAudioManagerVisible(false)}
                course={audioCourse}
                token={user ? user.token : ""}
                toastRef={toastRef}
            />
            <CourseReviewModal
                visible={!!reviewCourse}
                course={reviewCourse}
                token={user ? user.token : ""}
                onClose={handleReviewModalClose}
            />
            <CourseDurationModal
                visible={!!durationCourse}
                course={durationCourse}
                token={user ? user.token : ""}
                onClose={handleDurationModalClose}
            />
            <CourseKeywordsModal
                visible={!!keywordsCourse}
                course={keywordsCourse}
                token={user ? user.token : ""}
                onClose={handleKeywordsModalClose}
            />
        </div>

    );

}
